/**
 * Field Export Mappings Editor Component
 * Allows configuration of which property to export for reference fields
 */

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import { ExportTemplate } from '@/types/exportTemplates';

interface FieldExportMappingsEditorProps {
  template: ExportTemplate;
  onChange: (template: ExportTemplate) => void;
}

const REFERENCE_TYPES = [
  { 
    key: 'glossaryItem', 
    label: 'Glossary Items (Object References)',
    description: 'When a field references another object from your library'
  },
  { 
    key: 'types', 
    label: 'Types',
    description: 'When a field references a type'
  },
  { 
    key: 'tags', 
    label: 'Tags',
    description: 'When a field references a tag'
  }
];

const EXPORT_PROPERTIES = [
  { value: 'id', label: 'ID (Unique Identifier)' },
  { value: 'name', label: 'Name (Display Name)' },
  { value: 'code_id', label: 'Code ID (Code Reference)' }
];

export const FieldExportMappingsEditor = ({ template, onChange }: FieldExportMappingsEditorProps) => {
  const mappings = template.fieldExportMappings || {};

  const updateMapping = (fieldType: string, property: string) => {
    onChange({
      ...template,
      fieldExportMappings: {
        ...mappings,
        [fieldType]: property
      }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold mb-2 block">Reference Field Export Settings</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Configure which property to export when a field references another object. For example, when a field references a Glossary Item, you can choose to export its ID, Name, or Code ID.
        </p>
      </div>

      <div className="space-y-3">
        {REFERENCE_TYPES.map(({ key, label, description }) => (
          <div key={key} className="p-4 border border-border rounded bg-accent/5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium">{label}</Label>
                <p className="text-xs text-muted-foreground mt-1">{description}</p>
              </div>
              <div className="w-64">
                <Select
                  value={mappings[key] || 'name'}
                  onValueChange={(value) => updateMapping(key, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPORT_PROPERTIES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-3 bg-muted/30 rounded text-xs text-muted-foreground">
        <strong>Example:</strong> If you set Glossary Items to export "Name" and have a field that references an NPC named "Village Elder", the exported code will contain "Village Elder" instead of the NPC's ID.
      </div>
    </div>
  );
};
